/* ============================================================
   OVÉE SPIN BOTTLE — an interactive 3D bottle, in plain JavaScript and WebGL (no libraries).
   Markup: snippets/ovee-spin-bottle.liquid. Styles: assets/ovee-spin.css.

   One fragment shader ray-marches the airless bottle (white body, frosted over-cap flush on
   top, and the white pump base and actuator seen through it) and wraps the real print label round it (assets/ovee-spin-label-*.webp, made
   from the label PDFs). Studio lighting: two tall softboxes and a top light reflect in the
   gloss, so the highlights stay put while the label turns, as on a real turntable. The cap
   is genuinely see-through (a second ray continues behind it), and the canvas is
   transparent apart from a soft contact shadow, so the bottle sits on any background.

   Like a car in a showroom, it turns slowly on its own (about one turn every 25 seconds,
   each bottle starting at a different angle) while it is on screen. Hovering with a mouse,
   keyboard focus, or a drag stops the turn so the label can be read; it drifts back into
   motion a few seconds later. Drag (or swipe) to spin, with inertia; drag up and down to tilt
   a little (not with data-no-tilt, where it stands on a drawn table); arrow keys turn it 15° a step; double-click or Home returns to the front. The
   slow turn draws at 30fps (it looks the same) at full sharpness; nothing draws off screen.

   Zoom (31 Oct 2026): two fingers pinch the bottle in and out (1x to 3x) about the point between
   them, and moving both fingers slides the view over the label; a trackpad pinch (ctrl + wheel) and
   the + and - keys do the same. Zoomed in, an up-and-down drag moves the view rather than tilting.
   The zoom is drawn, not scaled, so it stays sharp, and a bottle using the half-size label texture
   fetches the full-size one the first time it is zoomed in. Zoomed in, the bottle does not turn on
   its own. Pinching out past 1x recentres it; double-click or Home resets it too. The page itself
   never zooms or scrolls from a two-finger gesture on the bottle.
   Reduced motion: no turning on its own. No WebGL: the fallback image in the markup stays.

   Embed mode (data-mode="embed", the homepage "Whose skin" routes): the bottle stands in
   place of a photo inside a link card; the photo stays underneath as the fallback. It is
   decorative there (the link is the control): a tap or click follows the link, a drag spins
   the bottle instead, hovering the card pauses it, and it turns a little more as the page
   scrolls past.
   ============================================================ */
(function () {
  'use strict';

  var BOTTLES = {
    cream150: { body: 1.265, y0: 0.045, y1: 1.205, front: 0.75, tex: 'cream150' },
    cream50: { body: 0.62, y0: 0.040, y1: 0.560, front: 0.49, tex: 'cream50' },
    oatwash: { body: 1.265, y0: 0.045, y1: 1.205, front: 0.75, tex: 'oatwash' },
    lipid: { body: 1.265, y0: 0.045, y1: 1.205, front: 0.705, tex: 'lipid' }
  };
  var CAP_TOP = 0.40;    // cap top above the body
  var TURN = Math.PI * 2;
  var SPEED = 0.25;      // the showroom turn, radians a second (~25s a turn)
  var RESUME = 2500;     // ms after the last touch before it turns again
  var MAX_ZOOM = 3;      // pinch zoom, 1x to 3x (further and the label texture would blur)

  // Safety net for slower phones, shared by every bottle on the page: if the browser can't keep
  // up while bottles move (frames arriving slower than ~22fps for 2s in all), step down once to
  // 1x resolution, and if it still can't, stop the turning (the bottles rest facing front).
  var QUALITY = 0, slowFor = 0, lastTick = -1, ALL = [];
  function noteFrameGap(now, gap) {
    if (now === lastTick) return;                   // bottles drawn in the same frame count once
    lastTick = now;
    if (QUALITY >= 2 || gap > 250) return;          // a long gap = tab hidden or paused, not slowness
    slowFor = gap > 45 ? slowFor + gap : Math.max(0, slowFor - gap * 0.5);
    if (slowFor < 2000) return;
    slowFor = 0;
    QUALITY++;
    document.documentElement.setAttribute('data-ovee-spin-quality', String(QUALITY));
    ALL.forEach(function (v) {
      if (QUALITY >= 2 && v.turntable) { v.turntable = false; v.spinSpeed = 0; if (v.texReady) v.faceTo(0, 900); }
      else v.request();
    });
  }
  function clamp(x, a, b) { return Math.max(a, Math.min(b, x)); }

  var VERT = 'attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.0,1.0);}';

  var FRAG = `
precision highp float;
uniform vec2 uRes;
uniform vec4 uCam;     // bottle yaw, camera pitch, distance, focal length
uniform vec4 uShape;   // body height, label bottom, label top, label front (u)
uniform vec4 uMisc;    // texture ready, samples, shadow strength, target y
uniform vec3 uView;    // view offset x, y (in units of the view height, y up) and zoom
uniform sampler2D uTex;

#define PI 3.14159265
float gM;
float gSkip;

vec2 r2(vec2 v, float a){ float c = cos(a), s = sin(a); return vec2(c*v.x - s*v.y, s*v.x + c*v.y); }
float smin(float a, float b, float k){ float h = max(k - abs(a - b), 0.0)/k; return min(a, b) - h*h*k*0.25; }
float smax(float a, float b, float k){ return -smin(-a, -b, k); }
float sdCyl(vec3 p, float r, float h, float rr){ vec2 d = vec2(length(p.xz) - r + rr, abs(p.y) - h + rr); return min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - rr; }
float sdBox(vec3 p, vec3 b, float rr){ vec3 q = abs(p) - b + rr; return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0) - rr; }
// A solid of revolution from y0 to y1 of radius R, with its own rounding at the top (rt) and
// bottom (rb) corners: the bottle's top edge crisp and its base soft, the cap round only on top
float sdTube(vec3 p, float R, float y0, float y1, float rt, float rb){
  vec2 q = vec2(length(p.xz), p.y - 0.5*(y0 + y1));
  float rr = q.y > 0.0 ? rt : rb;
  vec2 e = abs(q) - vec2(R, 0.5*(y1 - y0)) + rr;
  return min(max(e.x, e.y), 0.0) + length(max(e, 0.0)) - rr;
}

// The airless bottle, as photographed: a white body; a clear over-cap the same width,
// sitting flush on the body's top; inside it the white pump base (two grooves) and the
// actuator, whose top scoops down to a small spout at the front.
// Proportions measured from Chris's photos (IMG_4340-4344, 16 Oct), in bottle radii.
float bottle(vec3 l){
  float H = uShape.x;
  // the body: a crisp top edge, so the cap sits flush on it
  float d = sdTube(l, 0.24, 0.0, H, 0.004, 0.014); gM = 2.0;
  // everything else sits on top of the body: well below it (most of the label) skip the pump
  // and cap, returning a safe lower bound for them instead (they lie above H - 0.01, within
  // the cap's radius)
  if (l.y < H - 0.03) return min(d, max(H - 0.012 - l.y, length(l.xz) - 0.237));
  // pump base: a lower ring nearly filling the cap, with a fine groove near the bottom and a
  // double line at the step...
  float rl = length(l.xz);
  float base = sdTube(l, 0.226, H - 0.01, H + 0.052, 0.003, 0.0);
  float gr = abs(rl - 0.226);
  base = smax(base, -max(gr - 0.004, abs(l.y - (H + 0.014)) - 0.0026), 0.0015);
  base = smax(base, -max(gr - 0.004, abs(l.y - (H + 0.041)) - 0.0026), 0.0015);
  // ...under a slightly narrower sleeve with a rounded lip, which the actuator rises out of
  float ringA = sdTube(l, 0.214, H + 0.05, H + 0.136, 0.010, 0.002);
  base = min(base, smax(ringA, -(rl - 0.2005), 0.003));
  if (base < d){ d = base; gM = 4.0; }
  // the actuator rises out of the upper ring: below the ring's hollow, a lower bound will do
  if (l.y < H + 0.045) d = min(d, max(H + 0.05 - l.y, rl - 0.2005));
  else {
    // the actuator (lid-off photos IMG_0582-0598): a wide stem with a slight draft, topped by a
    // finger pad tilted ~9 degrees up towards the spout, with a shallow dish and a soft rounded
    // edge. One crisp crease runs round it: at the front it is the bottom edge of a broad
    // concave scoop under the pad's overhanging lip, round the sides and back it is a fine step,
    // dipping lowest at the back. The nozzle is a small shallow slot in the front of the lip.
    // the spout faces the front (the label's side), as in every studio photo (Chris, 19 Oct 2026; was -0.9, front-left)
    vec3 a = l; a.xz = r2(a.xz, 0.0);
    float y0 = H + 0.134, yc = H + 0.335, sl = 0.16;   // the spout ~0.037 under the cap top (IMG_0599)
    float ra = max(length(a.xz), 1e-4);
    float cz = a.z/ra;                                   // cos of the angle from the spout
    float k = clamp((a.y - y0)/0.23, 0.0, 1.0);
    float hl = y0 + 0.0875 + 0.025*cz - 0.0225*(2.0*cz*cz - 1.0);   // the crease
    // the stem: the fine step at the crease; at the front, above it, the scoop (facing the
    // spout, fading out by the sides) eases in to its full depth under the lip
    float w = max(cz, 0.0); w *= w;
    float rs = mix(0.196, 0.187, k) + 0.0045*smoothstep(-0.0015, 0.0015, a.y - hl)
             - 0.022*w*smoothstep(0.0, 1.0, clamp((a.y - hl)/0.055, 0.0, 1.0));
    float pad = (a.y - yc - sl*a.z)*0.9874;
    float stem = smax(length(a.xz) - rs, pad, 0.03)*0.8;
    // the lip: the pad's top plate, a little wider at the front so it overhangs the scoop,
    // with rounded edges, blended into the scoop beneath
    vec3 ae = a; if (ae.z > 0.0) ae.z /= 1.1;
    float plate = smax(abs(pad + 0.017) - 0.017, length(ae.xz) - 0.187, 0.02);
    float act = smin(stem, plate, 0.012);
    act = smax(act, -(length(a - vec3(0.0, yc, 0.0) - vec3(0.0, 0.9874, -0.158)*0.6) - 0.609), 0.01);
    // the nozzle slot, just under the lip's top edge
    act = smax(act, -sdBox(a - vec3(0.0, yc + sl*0.205 - 0.019, 0.21), vec3(0.028, 0.011, 0.012), 0.003), 0.002);
    if (act < d){ d = act; gM = 4.0; }
  }
  if (gSkip < 0.5){
    // the clear cap: straight walls, a hair narrower than the body, round only at the top
    float cap = sdTube(l, 0.2365, H, H + 0.40, 0.022, 0.0025);
    if (cap < d){ d = cap; gM = 5.0; }
  }
  return d;
}

float map(vec3 p){
  vec3 l = p; l.xz = r2(l.xz, uCam.x);
  float b = bottle(l);
  float m = gM;
  float d = p.y;
  if (b < d){ d = b; } else { m = 1.0; }
  gM = m;
  return d;
}

vec3 nor(vec3 p){
  const vec2 k = vec2(1.0, -1.0); float e = 0.0005;
  return normalize(k.xyy*map(p + k.xyy*e) + k.yyx*map(p + k.yyx*e) + k.yxy*map(p + k.yxy*e) + k.xxx*map(p + k.xxx*e));
}

float march(vec3 ro, vec3 rd){
  float t = 0.0;
  for (int i = 0; i < 110; i++){
    float h = map(ro + rd*t);
    if (h < 0.0003*(1.0 + t)) return t;
    t += h;
    if (t > 30.0) break;
  }
  return -1.0;
}

float shadow(vec3 ro, vec3 rd){
  float res = 1.0; float t = 0.015;
  for (int i = 0; i < 40; i++){
    float h = map(ro + rd*t);
    res = min(res, 7.0*h/t);
    t += clamp(h, 0.01, 0.15);
    if (res < 0.002 || t > 4.0) break;
  }
  return clamp(res, 0.0, 1.0);
}

float occl(vec3 p, vec3 n){
  float o = 0.0, s = 1.0;
  for (int i = 0; i < 5; i++){
    float h = 0.01 + 0.06*float(i);
    o += (h - map(p + n*h))*s; s *= 0.7;
  }
  return clamp(1.0 - 2.6*o, 0.0, 1.0);
}

vec3 lin(vec3 c){ return pow(c, vec3(2.2)); }
float acesL(float x){ return clamp((x*(2.51*x + 0.03))/(x*(2.43*x + 0.59) + 0.14), 0.0, 1.0); }
// tone map on luminance, so print colours keep their hue (orange stays orange, not lemon)
vec3 aces(vec3 c){
  float L = dot(c, vec3(0.2126, 0.7152, 0.0722));
  vec3 o = c*(acesL(L)/max(L, 1e-4));
  float mx = max(o.r, max(o.g, o.b));
  return mx > 1.0 ? mix(o/mx, vec3(1.0), clamp((mx - 1.0)*0.35, 0.0, 0.5)) : o;
}

// The studio, as seen in reflections: a soft gradient, a big softbox on the left, a narrow
// one on the right and a light overhead. Fixed to the camera, so they stay put as it turns.
vec3 env(vec3 r){
  vec3 c = mix(vec3(0.42, 0.40, 0.38), vec3(0.92, 0.91, 0.90), smoothstep(-0.35, 0.55, r.y));
  float az = atan(r.x, r.z);
  float band = smoothstep(-0.30, -0.05, r.y)*smoothstep(0.95, 0.62, r.y);
  c += vec3(5.0, 4.9, 4.7)*smoothstep(0.20, 0.12, abs(az + 1.05))*band;
  c += vec3(2.4, 2.45, 2.55)*smoothstep(0.075, 0.035, abs(az - 0.98))*band;
  c += vec3(1.6)*smoothstep(0.72, 0.96, r.y);
  return c;
}

vec3 surface(vec3 p, vec3 n, vec3 rd, float m){
  vec3 l = p; l.xz = r2(l.xz, uCam.x);
  vec3 alb = lin(vec3(0.93, 0.925, 0.915));
  float refl = 1.0; float F0 = 0.05; float spec = 0.6; float gloss = 90.0;
  if (m < 2.5){
    float v = (uShape.z - l.y)/(uShape.z - uShape.y);
    if (v > 0.0 && v < 1.0 && uMisc.x > 0.5){
      float u1 = fract(uShape.w + atan(l.x, l.z)/(2.0*PI));
      float u = u1;
#ifdef HAS_DERIV
      float u2 = fract(u1 + 0.5) - 0.5;
      u = fwidth(u1) <= fwidth(u2) + 1e-6 ? u1 : u2;
#endif
      alb = lin(texture2D(uTex, vec2(u, v)).rgb);
      refl = 0.55; F0 = 0.04; spec = 0.35; gloss = 50.0;
      float edge = smoothstep(0.0, 0.004, v)*smoothstep(1.0, 0.996, v);
      alb *= mix(0.82, 1.0, edge);
    }
  } else if (m < 4.5){
    alb = lin(vec3(0.95, 0.945, 0.935)); refl = 0.7; spec = 0.45; gloss = 60.0;
  }
  vec3 LK = normalize(vec3(-0.5, 1.0, 0.62));
  vec3 LF = normalize(vec3(0.85, 0.28, 0.45));
  float sh = shadow(p + n*0.004, LK);
  float oc = occl(p, n);
  float ndl = clamp(dot(n, LK), 0.0, 1.0);
  vec3 col = alb*ndl*sh*vec3(1.0, 0.97, 0.93)*2.1;
  col += alb*clamp(dot(n, LF), 0.0, 1.0)*vec3(0.95, 0.97, 1.0)*0.55*oc;
  col += alb*(0.55 + 0.45*n.y)*vec3(1.0, 0.98, 0.96)*0.42*oc;
  float ndv = clamp(dot(n, -rd), 0.0, 1.0);
  float F = F0 + (1.0 - F0)*pow(1.0 - ndv, 5.0);
  col += refl*env(reflect(rd, n))*F*mix(0.6, 1.0, oc);
  vec3 hv = normalize(LK - rd);
  col += spec*pow(clamp(dot(n, hv), 0.0, 1.0), gloss)*(gloss + 8.0)/30.0*sh*ndl*vec3(1.0, 0.97, 0.94);
  return col;
}

// One ray: premultiplied colour and coverage, ready for the page behind
vec4 shadeHit(vec3 ro, vec3 rd, float t){
  vec3 p = ro + rd*t;
  map(p); float m = gM;
  vec3 n = nor(p);
  if (m < 1.5){
    float sh = shadow(p + vec3(0.0, 0.002, 0.0), normalize(vec3(-0.5, 1.0, 0.62)));
    float oc = occl(p, vec3(0.0, 1.0, 0.0));
    float a = uMisc.z*clamp((1.0 - sh)*0.55 + (1.0 - oc)*0.9, 0.0, 1.0);
    a *= smoothstep(0.78, 0.27, length(p.xz));
    return vec4(vec3(0.20, 0.12, 0.08)*a, a);
  }
  return vec4(pow(aces(surface(p, n, rd, m)*0.8), vec3(1.0/2.2)), 1.0);
}

vec4 trace(vec3 ro, vec3 rd){
  gSkip = 0.0;
  float t = march(ro, rd);
  if (t < 0.0) return vec4(0.0);
  vec3 p = ro + rd*t;
  map(p);
  if (gM < 4.5) return shadeHit(ro, rd, t);
  // the over-cap, frosted like the real one (19 Oct 2026, matched to the studio photos): soft reflections,
  // a milky haze, and the pump behind it stays opaque white (the page never shows through the pump)
  vec3 n = nor(p);
  float ndv = clamp(dot(n, -rd), 0.0, 1.0);
  float F = 0.04 + 0.96*pow(1.0 - ndv, 5.0);
  vec3 R = pow(aces(env(reflect(rd, n))*F*0.55), vec3(1.0/2.2));
  // the thin wall seen edge-on: a fine bright line at the outline, not a thick glassy rim
  float rim = pow(1.0 - ndv, 7.0);
  // and the hairline where the cap meets the bottle
  vec3 lc = p; lc.xz = r2(lc.xz, uCam.x);
  float seam = smoothstep(0.005, 0.0, lc.y - uShape.x);
  gSkip = 1.0;
  vec3 ro2 = p + rd*0.003;   // just past the cap: the pump base sits only ~0.01 inside it
  float t2 = march(ro2, rd);
  vec4 behind = vec4(0.0);
  if (t2 >= 0.0) behind = shadeHit(ro2, rd, t2);
  gSkip = 0.0;
  float T = (1.0 - F)*0.42;
  vec3 haze = vec3(0.94, 0.95, 0.97);
  vec3 c = (behind.rgb*T + haze*0.50 + R + vec3(0.90, 0.92, 0.95)*0.35*rim)*(1.0 - 0.3*seam);
  float a = clamp(max(behind.a, 0.50 + 0.4*rim + 0.35*seam + max(R.r, max(R.g, R.b))*0.6), 0.0, 1.0);
  a = max(a, max(c.r, max(c.g, c.b)));
  return vec4(min(c, vec3(a)), a);
}

void main(){
  vec4 acc = vec4(0.0);
  int N = int(uMisc.y);
  vec3 ta = vec3(0.0, uMisc.w, 0.0);
  float pit = uCam.y;
  vec3 ro = ta + uCam.z*vec3(0.0, sin(pit), cos(pit));
  vec3 fw = normalize(ta - ro); vec3 rt = normalize(cross(fw, vec3(0.0, 1.0, 0.0))); vec3 up = cross(rt, fw);
  for (int s = 0; s < 4; s++){
    if (s >= N) break;
    vec2 off = N > 1 ? vec2(mod(float(s), 2.0), floor(float(s)/2.0))*0.5 - 0.25 : vec2(0.0);
    vec2 uv = ((gl_FragCoord.xy + off - 0.5*uRes)/uRes.y - uView.xy)/uView.z;
    vec3 rd = normalize(uv.x*rt + uv.y*up + uCam.w*fw);
    acc += trace(ro, rd);
  }
  gl_FragColor = acc/float(N);
}
`;

  function Viewer(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-spin-canvas]');
    this.stage = root.querySelector('[data-spin-stage]');
    this.hint = root.querySelector('[data-spin-hint]');
    this.cfg = BOTTLES[root.getAttribute('data-bottle')] || BOTTLES.cream150;
    this.texUrl = root.getAttribute('data-texture');
    // the full-size label texture, fetched the first time a half-size bottle is zoomed in
    this.texHd = root.getAttribute('data-texture-hd');
    this.zoom = 1; this.panX = 0; this.panY = 0;
    this.reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.yaw = 0; this.pitch = 0.12;
    // data-no-tilt: the bottle stands on a drawn studio table (a CSS backdrop that cannot tilt), so dragging only
    // turns it; tilting the camera would slide its shadow off the table (19 Oct 2026)
    this.noTilt = root.hasAttribute('data-no-tilt');
    this.vYaw = 0; this.vPitch = 0;
    this.texReady = 0; this.drawn = false;
    this.raf = 0; this.inView = false; this.touched = false; this.lastInput = -1e9;
    this.turntable = !this.reduce; this.spinSpeed = 0; this.hovering = false; this.focused = false;
    this.embed = root.getAttribute('data-mode') === 'embed';
    this.scrollYaw = 0; this.spinId = 0;
  }

  Viewer.prototype.init = function () {
    var gl;
    try {
      gl = this.canvas.getContext('webgl', { antialias: false, alpha: true, premultipliedAlpha: true, depth: false, stencil: false, powerPreference: 'default' });
    } catch (e) { gl = null; }
    if (!gl) return this.fail();
    this.gl = gl;
    var deriv = gl.getExtension('OES_standard_derivatives');
    var src = (deriv ? '#extension GL_OES_standard_derivatives : enable\n#define HAS_DERIV 1\n' : '') + FRAG;
    try {
      var prog = gl.createProgram();
      [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, src]].forEach(function (s) {
        var sh = gl.createShader(s[0]);
        gl.shaderSource(sh, s[1]); gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh));
        gl.attachShader(prog, sh);
      });
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
      gl.useProgram(prog);
      this.prog = prog;
    } catch (err) {
      if (window.console) console.warn('Ovée spin bottle: WebGL shader failed, showing the image.', err);
      return this.fail();
    }
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(this.prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var U = this.U = {};
    var prog2 = this.prog;
    ['uRes', 'uCam', 'uShape', 'uMisc', 'uView', 'uTex'].forEach(function (n) { U[n] = gl.getUniformLocation(prog2, n); });
    this.tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([240, 238, 234, 255]));
    gl.uniform1i(U.uTex, 0);
    var self = this;
    this.canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); self.fail(); });
    this.bind();
    this.loadTexture();
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.request(); }).observe(this.stage);
    else window.addEventListener('resize', function () { self.request(); });
  };

  Viewer.prototype.fail = function () {
    this.root.classList.add('is-fallback');
    this.dead = true;
    var img = this.root.querySelector('.ovee-spin__fallback[data-src]');
    if (img && !img.getAttribute('src')) img.src = img.getAttribute('data-src');
  };

  // Load the label texture: the one in the markup, or (hd) the full-size one once zoomed in
  Viewer.prototype.loadTexture = function (url, hd) {
    var self = this, gl = this.gl, img = new Image();
    img.decoding = 'async';
    img.onload = function () {
      if (self.dead) return;
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, self.tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var an = gl.getExtension('EXT_texture_filter_anisotropic') || gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
      if (an) gl.texParameterf(gl.TEXTURE_2D, an.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(an.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      self.texReady = 1;
      self.root.classList.add('is-ready');
      self.request();
    };
    img.onerror = function () { if (!hd) self.fail(); };
    img.src = url || this.texUrl;
  };

  // Embed mode: a gentle turn with the page scroll (front when the bottle is mid-screen).
  // Returns true if the angle changed.
  Viewer.prototype.updateScrollYaw = function () {
    var y = 0;
    if (this.embed && !this.reduce) {
      var r = this.stage.getBoundingClientRect(), vh = window.innerHeight || 800;
      y = clamp((r.top + r.height / 2 - vh / 2) / vh, -1, 1) * 0.5;
    }
    var changed = Math.abs(y - this.scrollYaw) > 0.0005;
    this.scrollYaw = y;
    return changed;
  };

  Viewer.prototype.draw = function () {
    if (this.dead || !this.texReady) return;
    var gl = this.gl, U = this.U, c = this.cfg;
    var cssW = this.stage.clientWidth, cssH = this.stage.clientHeight;
    if (!cssW || !cssH) return;
    // embedded bottles move with the scroll: 1.5x while moving, 2x once they rest
    var dpr = Math.min(window.devicePixelRatio || 1, QUALITY ? 1 : this.embed && this.moving ? 1.5 : 2);
    var w = Math.round(cssW * dpr), h = Math.round(cssH * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
    gl.viewport(0, 0, w, h);
    var total = c.body + CAP_TOP, fl = 2.6;
    if (this.root.getAttribute('data-fit') === 'range') total = BOTTLES.cream150.body + CAP_TOP;
    var aspect = w / h;
    var fit = Math.max(total * (this.embed ? 1.24 : 1.12), (0.48 * 1.5) / Math.min(aspect, 1) * 1.0);
    var dist = fit * fl;
    gl.uniform2f(U.uRes, w, h);
    gl.uniform4f(U.uCam, this.yaw + this.scrollYaw, this.pitch, dist, fl);
    gl.uniform4f(U.uShape, c.body, c.y0, c.y1, c.front);
    gl.uniform3f(U.uView, this.panX, this.panY, this.zoom);
    var samples = dpr >= 1.5 || QUALITY ? 1 : (this.moving ? 1 : this.turning ? 2 : 4);
    // embedded: stand lower in the frame, as in the studio photos it replaces
    gl.uniform4f(U.uMisc, this.texReady, samples, 0.55, total * (this.embed ? 0.55 : 0.47));
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (!this.drawn) { this.drawn = true; this.root.classList.add('is-drawn'); }
  };

  Viewer.prototype.request = function () {
    if (this.dead) return;
    var self = this;
    if (!this.raf) this.raf = requestAnimationFrame(function (now) { self.raf = 0; self.frame(now); });
  };

  Viewer.prototype.frame = function (now) {
    var self = this;
    // how fast frames arrive while this bottle keeps asking for them (the safety net above)
    if (this.chain) noteFrameGap(now, now - this.lastRaf);
    this.lastRaf = now; this.chain = false;
    // zoomed in, someone is reading: no turning until it is back at 1x
    var paused = this.dragging || this.hovering || this.focused || this.zoom > 1 || now - this.lastInput < RESUME;
    var target = this.turntable && !paused ? SPEED : 0;
    var inertia = !this.dragging && (Math.abs(this.vYaw) > 0.0004 || Math.abs(this.vPitch) > 0.0004);
    var scrolling = now - (this.scrolledAt || 0) < 150;
    var turning = this.turntable && (target > 0 || this.spinSpeed > 0);
    // the slow turn on its own looks the same at 30fps: skip every other frame
    if (turning && !inertia && !this.dragging && !scrolling && this.lastDraw && now - this.lastDraw < 30) { this.chain = true; this.request(); return; }
    var dt = this.lastFrame ? Math.min((now - this.lastFrame) / 1000, 0.05) : 0.016;
    this.lastFrame = now;
    this.updateScrollYaw();
    if (inertia) {
      this.yaw += this.vYaw * dt * 60;
      this.pitch = clampPitch(this.pitch + this.vPitch * dt * 60);
      var decay = Math.pow(0.92, dt * 60);
      this.vYaw *= decay; this.vPitch *= decay;
    }
    if (this.turntable) {
      // ease in and out of the turn, like a turntable motor
      this.spinSpeed += (target - this.spinSpeed) * Math.min(1, dt * 1.5);
      if (target === 0 && this.spinSpeed < 0.003) this.spinSpeed = 0;
      this.yaw += this.spinSpeed * dt;
      if (target > 0) this.pitch += (0.12 - this.pitch) * Math.min(1, dt * 1.5);
    }
    this.moving = inertia || this.dragging || scrolling;
    this.turning = turning;
    if (paused || !turning) this.updateAria();
    this.draw();
    this.lastDraw = now;
    if ((inertia || turning) && this.inView) { this.chain = true; this.request(); }
    else if (!this.dragging) {
      // once it settles, one sharper (supersampled) frame
      clearTimeout(this.sharpT);
      this.sharpT = setTimeout(function () { self.moving = false; self.turning = false; self.draw(); }, 120);
    }
    // paused after a touch: wake up when it is time to turn again
    clearTimeout(this.resumeT);
    if (this.turntable && this.inView && !this.dragging && !this.hovering && !this.focused && now - this.lastInput < RESUME) {
      this.resumeT = setTimeout(function () { self.request(); }, RESUME - (now - this.lastInput) + 30);
    }
  };

  function clampPitch(p) { return Math.max(-0.05, Math.min(0.55, p)); }

  Viewer.prototype.updateAria = function () {
    if (this.embed) return;   // decorative inside a link: the link is the control
    var deg = Math.round(((this.yaw * 180 / Math.PI) % 360 + 360) % 360);
    if (deg === this.lastDeg) return;
    this.lastDeg = deg;
    var side = deg < 30 || deg > 330 ? 'front' : deg < 150 ? 'left side' : deg <= 210 ? 'back' : 'right side';
    this.stage.setAttribute('aria-valuenow', String(deg));
    this.stage.setAttribute('aria-valuetext', deg + ' degrees, ' + side);
  };

  Viewer.prototype.touch = function () {
    this.lastInput = performance.now();
    if (!this.touched) {
      this.touched = true;
      this.root.classList.add('is-touched');
    }
  };

  Viewer.prototype.bind = function () {
    var self = this, st = this.stage, lastX = 0, lastY = 0, lastT = 0, startX = 0, startY = 0, axis = null, moved = 0;
    // every finger (or the mouse) down on the stage, by pointer id; two fingers make a pinch
    var pts = {}, pinch = null;
    function count() { var n = 0, k; for (k in pts) n++; return n; }
    function pinchOf() {
      var a = null, b = null, k;
      for (k in pts) { if (!a) a = pts[k]; else if (!b) b = pts[k]; }
      if (!a || !b) return null;
      return { d: Math.max(Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y)), 1), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    st.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      try { st.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
      self.touch();
      self.spinId++;
      self.vYaw = 0; self.vPitch = 0;
      if (count() >= 2) {
        // a second finger: pinch to zoom (and slide the view) until a finger lifts; never a tap
        pinch = pinchOf(); axis = 'pinch'; moved = 99;
        self.dragging = true;
        st.classList.add('is-grabbing');
        return;
      }
      self.dragging = true; axis = e.pointerType === 'mouse' ? 'free' : null; moved = 0;
      startX = lastX = e.clientX; startY = lastY = e.clientY; lastT = performance.now();
      st.classList.add('is-grabbing');
    });
    st.addEventListener('pointermove', function (e) {
      if (pts[e.pointerId]) { pts[e.pointerId].x = e.clientX; pts[e.pointerId].y = e.clientY; }
      if (axis === 'pinch') {
        var pz = pinchOf();
        if (!pz || !pinch) return;
        self.zoomAt(pz.x, pz.y, pz.d / pinch.d, pz.x - pinch.x, pz.y - pinch.y);
        pinch = pz;
        return;
      }
      if (axis === 'done' || !self.dragging) return;
      moved = Math.max(moved, Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY));
      // on touch, let a mostly vertical swipe scroll the page instead
      if (!axis) {
        var ax = Math.abs(e.clientX - startX), ay = Math.abs(e.clientY - startY);
        if (ax < 6 && ay < 6) return;
        axis = ax >= ay ? 'free' : 'scroll';
        if (axis === 'scroll') { self.dragging = false; st.classList.remove('is-grabbing'); return; }
      }
      var w = st.clientWidth || 300, now = performance.now();
      // zoomed in, the drag follows the magnified label: the turn slows and up-and-down slides the view
      var dx = (e.clientX - lastX) / w * 4.2 / self.zoom, dy = (e.clientY - lastY) / w * 1.6;
      if (self.zoom > 1) { self.pan(0, -(e.clientY - lastY) / (st.clientHeight || 300)); dy = 0; }
      else if (self.noTilt) dy = 0;
      self.yaw += dx; self.pitch = clampPitch(self.pitch + dy);
      var dtm = Math.max(now - lastT, 8) / 16.67;
      self.vYaw = dx / dtm; self.vPitch = dy / dtm * 0.5;
      lastX = e.clientX; lastY = e.clientY; lastT = now;
      self.lastInput = now;
      self.request();
    });
    var end = function (e) {
      delete pts[e.pointerId];
      if (axis === 'pinch' || axis === 'done') {
        // after a pinch the last finger just lifts: it must not fling the bottle
        pinch = null; axis = count() ? 'done' : null;
        self.vYaw = 0; self.vPitch = 0;
      }
      if (!self.dragging) return;
      self.dragging = false;
      st.classList.remove('is-grabbing');
      if (performance.now() - lastT > 80) { self.vYaw = 0; self.vPitch = 0; }
      self.request();
    };
    st.addEventListener('pointerup', end);
    st.addEventListener('pointercancel', end);
    st.addEventListener('lostpointercapture', end);
    // two fingers on the bottle zoom the bottle, never the page (touch-action: pan-y asks for this
    // too, but iOS Safari only listens to a cancelled touchmove / gesture)
    st.addEventListener('touchmove', function (e) { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });
    st.addEventListener('gesturestart', function (e) { e.preventDefault(); });
    // a trackpad pinch (or ctrl + wheel) zooms about the pointer; a plain wheel still scrolls the page
    st.addEventListener('wheel', function (e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      var d = e.deltaMode === 1 ? e.deltaY * 20 : e.deltaY;
      self.zoomAt(e.clientX, e.clientY, clamp(Math.exp(-d * 0.01), 0.7, 1.4), 0, 0);
    }, { passive: false });
    // a mouse over the bottle (or over its whole card, embedded) stops the turn to read it
    var host = this.embed ? (this.root.closest('a') || this.root.parentNode) : st;
    host.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      self.hovering = true; self.request();
    });
    host.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      self.hovering = false;
      self.lastInput = Math.max(self.lastInput, performance.now() - RESUME + 800);
      self.request();
    });
    if (this.embed) {
      var wrap = this.root.closest('a') || this.root.parentNode;
      // inside a link: a tap or click follows it, but not the click that ends a drag, and
      // dragging the bottle must not drag the link
      st.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      wrap.addEventListener('dragstart', function (e) { e.preventDefault(); });
      return;
    }
    // keyboard focus (not a mouse click) stops the turn too
    st.addEventListener('focus', function () {
      var kb = true;
      try { kb = st.matches(':focus-visible'); } catch (err) { /* older browsers */ }
      if (kb) { self.focused = true; self.request(); }
    });
    st.addEventListener('blur', function () {
      if (!self.focused) return;
      self.focused = false; self.lastInput = performance.now(); self.request();
    });
    st.addEventListener('dblclick', function () { self.touch(); self.faceTo(0, 0, true); });
    st.addEventListener('keydown', function (e) {
      var step = { ArrowRight: -15, ArrowLeft: 15, PageDown: -90, PageUp: 90 }[e.key];
      if (step) { self.touch(); self.spinTo(self.yaw + step * Math.PI / 180); e.preventDefault(); }
      else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && self.zoom > 1) { self.touch(); self.pan(0, e.key === 'ArrowUp' ? -0.1 : 0.1); e.preventDefault(); }
      else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !self.noTilt) { self.touch(); self.pitch = clampPitch(self.pitch + (e.key === 'ArrowUp' ? 0.08 : -0.08)); self.request(); e.preventDefault(); }
      else if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_') { self.zoomAt(null, null, e.key === '-' || e.key === '_' ? 1 / 1.25 : 1.25, 0, 0); e.preventDefault(); }
      else if (e.key === 'Home') { self.touch(); self.faceTo(0, 0, true); e.preventDefault(); }
    });
  };

  // Zoom by k about the screen point (cx, cy) (client px; the middle of the stage if null), then
  // slide the view by (dx, dy) client px. What is under the fingers stays under the fingers.
  Viewer.prototype.zoomAt = function (cx, cy, k, dx, dy) {
    var r = this.stage.getBoundingClientRect(), h = r.height || 1;
    var mx = cx == null ? 0 : (cx - (r.left + r.width / 2)) / h;
    var my = cy == null ? 0 : -(cy - (r.top + r.height / 2)) / h;
    var z = clamp(this.zoom * k, 1, MAX_ZOOM), s = z / this.zoom;
    this.panX = mx - (mx - this.panX) * s + (dx || 0) / h;
    this.panY = my - (my - this.panY) * s - (dy || 0) / h;
    this.zoom = z;
    this.clampPan();
    // zoomed in on the half-size label: fetch the full-size one (once) so the small print stays sharp
    if (z > 1.3 && this.texHd && !this.hdAsked) { this.hdAsked = true; this.loadTexture(this.texHd, true); }
    this.touch();
    this.request();
  };

  // Slide the view (view units: 1 = the stage height, y up)
  Viewer.prototype.pan = function (dx, dy) {
    this.panX += dx; this.panY += dy;
    this.clampPan();
    this.request();
  };

  // The view never leaves the 1x picture, so pinching out past 1x recentres the bottle
  Viewer.prototype.clampPan = function () {
    var st = this.stage, a = (st.clientWidth || 1) / (st.clientHeight || 1), m = (this.zoom - 1) / 2;
    this.panX = clamp(this.panX, -m * a, m * a);
    this.panY = clamp(this.panY, -m, m);
  };

  // Ease to an angle (and, with reset, back to 1x, centred). A newer spin (or a drag) cancels an older one.
  Viewer.prototype.spinTo = function (to, dur, reset) {
    var self = this, from = this.yaw, id = ++this.spinId, z0 = this.zoom, px0 = this.panX, py0 = this.panY;
    this.vYaw = 0; this.vPitch = 0;
    var t0 = performance.now();
    dur = this.reduce ? 1 : (dur || 450);
    var step = function (now) {
      if (id !== self.spinId) return;
      var f = Math.min((now - t0) / dur, 1), e = f < 0.5 ? 4 * f * f * f : 1 - Math.pow(-2 * f + 2, 3) / 2;
      self.yaw = from + (to - from) * e;
      if (reset) { self.zoom = z0 + (1 - z0) * e; self.panX = px0 * (1 - e); self.panY = py0 * (1 - e); }
      self.moving = f < 1;
      self.updateScrollYaw(); self.updateAria(); self.draw();
      if (f < 1) requestAnimationFrame(step); else { self.moving = false; self.draw(); }
    };
    requestAnimationFrame(step);
  };

  // Turn to the nearest angle that shows the same side as `angle` (0 = front, PI = back)
  Viewer.prototype.faceTo = function (angle, dur, reset) {
    this.spinTo(angle + Math.round((this.yaw - angle) / TURN) * TURN, dur, reset);
  };

  // Embedded bottles follow the page scroll: one frame per scroll frame, only while on screen
  var embeds = [], scrollRaf = 0;
  function onScroll() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(function () {
      scrollRaf = 0;
      embeds.forEach(function (v) {
        if (!v.inView || !v.texReady || v.dead) return;
        if (v.updateScrollYaw()) { v.scrolledAt = performance.now(); v.request(); }
      });
    });
  }

  var count = 0;
  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-ovee-spin]');
    Array.prototype.forEach.call(els, function (root) {
      if (root.__spin) return;
      var v = root.__spin = new Viewer(root);
      root.oveeSpin = v;
      ALL.push(v);
      if (QUALITY >= 2) v.turntable = false;
      // each bottle starts the turn at its own angle, so they never move in step; data-yaw
      // pins the start (the adult hero starts at the front to match its still poster)
      var y0 = parseFloat(root.getAttribute('data-yaw'));
      if (!v.reduce) v.yaw = isNaN(y0) ? -0.6 - (count % 4) * 0.8 : y0;
      count++;
      if (v.embed && !v.reduce) {
        if (!embeds.length) window.addEventListener('scroll', onScroll, { passive: true });
        embeds.push(v);
      }
      var started = false;
      var start = function () { if (started) return; started = true; v.init(); };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            v.inView = en.isIntersecting;
            if (en.isIntersecting) { start(); v.request(); }
          });
        }, { rootMargin: '250px 0px' }).observe(root);
      } else { v.inView = true; start(); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
